import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useCITranslate } from '../../i18n/customerIdentity';
import {
  bulkUpsertSettingValues,
  clearSettingValue,
  getEffectiveSettingValues,
  listSettingDefinitions,
  listSettingValues,
} from '../../services/customerIdentityApi';
import SettingValueEditor from './SettingValueEditor';
import {
  coerceValue,
  groupDefinitions,
  valueToEditorString,
  valuesEqual,
} from './settingsHelpers';

/**
 * Reusable settings editor embedded inside an entity form (Site, Customer,
 * User, Address). Renders only the settings whose allowed_scopes include
 * the panel's `scope`, grouped by `group_name`.
 *
 * Props:
 *   scope            'site' | 'customer' | 'user' | 'address'
 *   scopeId          existing entity id (null when the entity is being created)
 *   contextSiteId    for effective resolution (so users see what they will inherit)
 *   contextCustomerId
 *   contextUserId
 *   contextAddressId
 *   canEdit          disables inputs when false
 *   defaultExpanded  if true, opens the collapsible by default
 *   compact          if true, renders a tighter layout (no card chrome)
 *
 * Imperative handle (via ref):
 *   flush(newScopeId?)  → Promise<void>
 *     Upserts any pending drafts to the backend. Pass `newScopeId` after a
 *     create flow so we know which entity to attach the values to.
 *   hasDrafts() → boolean
 *   reset()
 */
const EntitySettingsPanel = forwardRef(function EntitySettingsPanel(
  {
    scope,
    scopeId = null,
    contextSiteId = null,
    contextCustomerId = null,
    contextUserId = null,
    contextAddressId = null,
    canEdit = true,
    defaultExpanded = false,
    compact = false,
    onError,
  },
  ref,
) {
  const t = useCITranslate();
  const [definitions, setDefinitions] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [effective, setEffective] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [resetTarget, setResetTarget] = useState(null);

  // Stabilize the parent-provided callback so it never participates in the
  // load effect's dependency list. Without this, an inline `onError` arrow
  // becomes a new reference on every parent render — re-running `loadAll`
  // and clobbering the user's in-progress drafts mid-edit.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const isCreating = !scopeId;

  const loadAll = useCallback(
    async ({ preserveDrafts = false } = {}) => {
      setLoading(true);
      try {
        const [defs, eff, overRows] = await Promise.all([
          listSettingDefinitions({ scope_type: scope }),
          getEffectiveSettingValues({
            ...(contextSiteId ? { site_id: contextSiteId } : {}),
            ...(contextCustomerId ? { customer_id: contextCustomerId } : {}),
            ...(contextUserId ? { user_id: contextUserId } : {}),
            ...(contextAddressId ? { address_id: contextAddressId } : {}),
          }),
          scopeId
            ? listSettingValues({ scope_type: scope, scope_id: scopeId })
            : Promise.resolve([]),
        ]);
        setDefinitions(Array.isArray(defs) ? defs : []);
        setEffective(Array.isArray(eff) ? eff : []);
        setOverrides(Array.isArray(overRows) ? overRows : []);
        if (!preserveDrafts) {
          setDrafts({});
        }
      } catch (err) {
        onErrorRef.current?.(err);
      } finally {
        setLoading(false);
      }
    },
    [
      scope,
      scopeId,
      contextSiteId,
      contextCustomerId,
      contextUserId,
      contextAddressId,
    ],
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const overridesById = useMemo(() => {
    const map = new Map();
    overrides.forEach((row) => map.set(String(row.setting_id), row));
    return map;
  }, [overrides]);

  const effectiveById = useMemo(() => {
    const map = new Map();
    effective.forEach((row) => map.set(String(row.setting_id), row));
    return map;
  }, [effective]);

  const grouped = useMemo(() => groupDefinitions(definitions), [definitions]);

  const dirtyEntries = useMemo(
    () => Object.entries(drafts).filter(([, d]) => d.dirty),
    [drafts],
  );

  // Mirror drafts into a ref so the imperative `flush` always reads the most
  // recent edits even when the parent's submit fires before React has had a
  // chance to commit the latest input event.
  const draftsRef = useRef(drafts);
  useEffect(() => {
    draftsRef.current = drafts;
  }, [drafts]);

  useImperativeHandle(
    ref,
    () => ({
      hasDrafts: () =>
        Object.values(draftsRef.current || {}).some((d) => d?.dirty),
      reset: () => setDrafts({}),
      flush: async (newScopeId) => {
        const currentDirty = Object.entries(draftsRef.current || {}).filter(
          ([, d]) => d?.dirty,
        );
        if (currentDirty.length === 0) return;
        const targetScopeId = newScopeId || scopeId;
        if (!targetScopeId) {
          throw new Error('settings_flush_requires_scope_id');
        }
        const items = currentDirty.map(([settingId, d]) => ({
          setting_id: settingId,
          scope_type: scope,
          scope_id: targetScopeId,
          value: d.newValue,
        }));
        await bulkUpsertSettingValues(items);
        if (String(targetScopeId) === String(scopeId)) {
          await loadAll();
        } else {
          setDrafts({});
        }
      },
    }),
    [loadAll, scope, scopeId],
  );

  const updateDraft = (definition, editorValue) => {
    const newValue = coerceValue(editorValue, definition.data_type, definition.input_type);
    setDrafts((prev) => {
      const override = overridesById.get(String(definition.id));
      const baseline = override ? override.value : null;
      const isOverrideExisting = Boolean(override);
      // For brand-new overrides, we treat any user input as dirty so the
      // first explicit value gets persisted.
      const dirty = isOverrideExisting
        ? !valuesEqual(newValue, baseline)
        : true;
      return {
        ...prev,
        [definition.id]: { definition, editorValue, newValue, dirty },
      };
    });
  };

  const removeOverride = async () => {
    if (!resetTarget || !scopeId) return;
    try {
      await clearSettingValue({
        settingId: resetTarget.id,
        scopeType: scope,
        scopeId,
      });
      setResetTarget(null);
      setDrafts((prev) => {
        const copy = { ...prev };
        delete copy[resetTarget.id];
        return copy;
      });
      await loadAll();
    } catch (err) {
      onErrorRef.current?.(err);
    }
  };

  if (definitions.length === 0 && !loading) {
    return null;
  }

  const headerSummary = isCreating
    ? t('settings.embedded.willBeAppliedAfterSave')
    : dirtyEntries.length === 0
      ? t('settings.values.noChanges')
      : t('settings.values.unsavedChanges', {
          count: dirtyEntries.length,
          plural: dirtyEntries.length === 1 ? '' : 's',
        });

  const renderRow = (definition) => {
    const override = overridesById.get(String(definition.id));
    const eff = effectiveById.get(String(definition.id));
    const draft = drafts[definition.id];

    const editorValue = draft
      ? draft.editorValue
      : valueToEditorString(
          override ? override.value : eff?.value ?? definition.default_value,
          definition.input_type,
          definition.data_type,
        );

    const sourceScope = override ? scope : eff?.source_scope || 'default';
    const overriddenHere = Boolean(override);

    return (
      <div className="ci-embedded-setting-row" key={definition.id}>
        <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
          <div>
            <label className="form-label fw-semibold mb-0">{definition.name}</label>
            {definition.is_required ? (
              <span className="text-danger ms-1">*</span>
            ) : null}
            <div className="d-flex flex-wrap gap-1 align-items-center small mt-1">
              <span className="text-muted">
                <i className="bi bi-link-45deg me-1" />
                {definition.setting_key}
              </span>
              <SourceBadge source={sourceScope} />
              {overriddenHere ? (
                <span
                  className="badge text-bg-light border"
                  title={t('settings.values.overridden')}
                >
                  <i className="bi bi-pin-angle me-1" />
                  {t('settings.values.overridden')}
                </span>
              ) : null}
              {draft?.dirty ? (
                <span className="badge text-bg-warning">
                  <i className="bi bi-pencil me-1" />
                  {t('settings.embedded.unsaved')}
                </span>
              ) : null}
            </div>
          </div>
          {overriddenHere && canEdit && !isCreating ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setResetTarget(definition)}
              title={t('settings.values.reset')}
            >
              <i className="bi bi-arrow-counterclockwise" />
            </button>
          ) : null}
        </div>

        <SettingValueEditor
          definition={definition}
          value={editorValue}
          onChange={(v) => updateDraft(definition, v)}
          disabled={!canEdit}
        />

        {definition.description ? (
          <div className="form-text small mt-1">{definition.description}</div>
        ) : null}
        {definition.help_text ? (
          <div className="form-text small">{definition.help_text}</div>
        ) : null}
      </div>
    );
  };

  const body = loading ? (
    <div className="text-muted small p-2">{t('common.loading')}</div>
  ) : (
    <div className="d-flex flex-column gap-3">
      {grouped.map((bucket) => (
        <div className="ci-embedded-setting-group" key={bucket.name}>
          <h6 className="mb-2 small text-uppercase text-muted">
            <i className="bi bi-folder me-1" />
            {bucket.name}
          </h6>
          <div className="row g-3">
            {bucket.items.map((d) => (
              <div className="col-12 col-md-6" key={d.id}>
                {renderRow(d)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className={compact ? '' : 'ci-card mt-3'}>
      <button
        type="button"
        className={`d-flex w-100 justify-content-between align-items-center gap-2 ${
          compact ? 'mb-2' : 'p-3'
        } ci-embedded-setting-toggle`}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div className="d-flex align-items-center gap-2">
          <i className={`bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
          <span className="fw-semibold">{t('settings.embedded.sectionTitle')}</span>
          <span className="badge text-bg-light border">{definitions.length}</span>
        </div>
        <span className={`small ${dirtyEntries.length > 0 ? 'text-warning' : 'text-muted'}`}>
          {headerSummary}
        </span>
      </button>
      {expanded ? (
        <div className={compact ? 'border rounded p-3 bg-light' : 'p-3 border-top'}>
          {isCreating ? (
            <div className="alert alert-info small py-2 mb-3">
              <i className="bi bi-info-circle me-1" />
              {t('settings.embedded.createHint')}
            </div>
          ) : null}
          {body}
        </div>
      ) : null}

      {resetTarget ? (
        <div
          className="modal d-block"
          role="dialog"
          aria-modal="true"
          style={{ background: 'rgba(0,0,0,.45)', zIndex: 1200 }}
          onClick={() => setResetTarget(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('settings.values.resetConfirmTitle')}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setResetTarget(null)}
                />
              </div>
              <div className="modal-body">
                {t('settings.values.resetConfirm', { scope: t(`settings.scopes.${scope}`) })}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setResetTarget(null)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={removeOverride}
                >
                  {t('settings.values.reset')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
});

function SourceBadge({ source }) {
  const t = useCITranslate();
  const map = {
    default: 'text-bg-light',
    global: 'text-bg-info',
    site: 'text-bg-primary',
    customer: 'text-bg-warning',
    user: 'text-bg-success',
    address: 'text-bg-dark',
  };
  return (
    <span
      className={`badge ${map[source] || 'text-bg-light'} text-uppercase`}
      style={{ fontSize: 10 }}
      title={t('settings.values.sourceHint', { source })}
    >
      {t(`settings.values.sourceBadge.${source}`)}
    </span>
  );
}

export default EntitySettingsPanel;
